import EditableCell from "@components/EditableCell"
import { NodeType, TableNodeProperties } from "@features/builder/graph/types"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import { getHeaders } from "@features/builder/NodeDrawer/NodeForm/NodeTableAccordion/getHeaders"

import {
  FormHelperText,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import { CUSTOM_ERROR_ATTRIBUTE } from "@utils/form"
import { useFormContext } from "react-hook-form"
import { getRows } from "@features/builder/NodeDrawer/NodeForm/NodeMnemonicTableAccordion/getRows"
import {
  ScenarioSourceType,
  useGetScenarioSourceMnemonicsQuery,
} from "@store/services/graphsService"
import { useMemo } from "react"
import { useGraphDocumentData } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocumentData"
import { GraphDetails } from "@features/builder/types"
import { getNormalisedGraphKey } from "@utils/graph"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import { ValidationType } from "@features/builder/graph/nodes/validation/types"

const MNEMONIC_TYPE_BY_NODE_TYPE: Record<
  NodeType.ForecastTable | NodeType.ScenarioTable,
  ScenarioSourceType
> = {
  [NodeType.ForecastTable]: "forecast",
  [NodeType.ScenarioTable]: "scenario",
}

const VALIDATION_KEY_BY_NODE_TYPE: Record<
  NodeType.ForecastTable | NodeType.ScenarioTable,
  ValidationType
> = {
  [NodeType.ForecastTable]: "FORECAST_MNEMONIC",
  [NodeType.ScenarioTable]: "SCENARIO_MNEMONIC",
}

export default function TableMnemonicTable(props: {
  nodeId: string
  onPropertiesUpdate: (properties: TableNodeProperties) => void
  nodeType: NodeType.ForecastTable | NodeType.ScenarioTable
}) {
  const form = useFormContext<NodeFormValues>()
  const properties = form.getValues("attributeProperties")

  const graphDocumentData = useGraphDocumentData()
  const graphData: GraphDetails | null = JSON.parse(graphDocumentData.data)
  const scenarioFilePath = graphData?.ScenarioSource
  const scenarioVersion = graphData?.ScenarioVersion
  const graphKey = graphData ? getNormalisedGraphKey(graphData) : ""

  const getMnemonicsQuery = useGetScenarioSourceMnemonicsQuery(
    {
      graphKey,
      type: MNEMONIC_TYPE_BY_NODE_TYPE[props.nodeType],
      // Include scenarioVersion to ensure query refetches when scenario file changes
      scenarioVersion: scenarioVersion ?? "",
    },
    {
      skip: !scenarioFilePath,
    },
  )

  const mnemonics = useMemo(
    () =>
      scenarioFilePath
        ? getMnemonicsQuery.data?.map(({ mnem }) => {
            return { label: mnem, value: mnem }
          }) ?? []
        : [],
    [getMnemonicsQuery.data, scenarioFilePath],
  )

  const headers = getHeaders(properties, form, props.onPropertiesUpdate)
  const rows = getRows(properties, headers, form, props.onPropertiesUpdate)

  const columnsRelatedError = form.formState.errors.attributeProperties?.columns?.message
  const rowsRelatedError = form.formState.errors.attributeProperties?.index?.message

  const { getNodeValidation } = useNodesValidation()
  const tableErrors = [
    columnsRelatedError,
    rowsRelatedError,
    ...(
      getNodeValidation(props.nodeId, VALIDATION_KEY_BY_NODE_TYPE[props.nodeType])?.errors ?? []
    ).map(({ msg }) => msg),
  ]

  return (
    <>
      <TableContainer>
        <Table size="small" padding="none">
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <EditableCell
                  {...header}
                  key={header.key}
                  control={form.control}
                  // @ts-ignore "string" is too generic to fit specific field names
                  name={header.name}
                  sx={{ px: ".15em" }}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {headers.map(({ idx }) => (
                  <EditableCell
                    key={idx}
                    {...row[idx]}
                    // @ts-ignore "string" is too generic to fit specific field names
                    name={row[idx].name}
                    control={form.control}
                    options={mnemonics}
                  />
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {tableErrors.filter(Boolean).map((error, index) => (
        <FormHelperText
          key={`${error}-${index}`}
          error
          {...{ [CUSTOM_ERROR_ATTRIBUTE]: Boolean(error) }}
        >
          - {error}
        </FormHelperText>
      ))}
    </>
  )
}
